import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { config } from '@/config/environment';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'root' | 'page' | 'component';
  enableRetry?: boolean;
  showContactSupport?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', true);
      scope.setLevel('error');
      scope.setExtra('errorInfo', errorInfo);
      scope.setExtra('level', this.props.level || 'component');
      Sentry.captureException(error);
    });
    
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { level = 'component', enableRetry = true, showContactSupport = true } = this.props;

      return (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Something went wrong
            </CardTitle>
            <CardDescription>
              {level === 'root' && 'The application encountered an unexpected error.'}
              {level === 'page' && 'This page encountered an error and needs to be refreshed.'}
              {level === 'component' && 'This section encountered an error.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {this.state.error && (
              <details className="text-sm text-muted-foreground">
                <summary className="cursor-pointer font-medium">Error details</summary>
                <pre className="mt-2 overflow-x-auto rounded bg-muted p-2">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              {enableRetry && (
                <Button onClick={this.handleRetry} variant="outline" size="sm">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              )}
              {level === 'root' && (
                <Button onClick={this.handleReload} size="sm">
                  Reload Page
                </Button>
              )}
              {showContactSupport && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`mailto:${config.app.supportEmail}?subject=Error Report`, '_blank')}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Contact Support
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}